import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Loading from "../components/Loading";
import FavHeart from "../components/FavHeart";
import ShareButton from "../components/ShareButton";
import { ArrowLeft, Clock, MapPin, User } from "lucide-react";
import { formatDateTime } from "../lib/format";

function fmtDuration(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return iso;
  const h = m[1] ? `${m[1]}h ` : "";
  const min = m[2] ? `${m[2]}m` : "";
  return (h + min).trim() || iso;
}

export default function SpacewalkDetailPage() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/spacewalks/${id}`)
      .then(r => setW(r.data))
      .catch(() => setW(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading message="ACCESSING EVA LOG" />;
  if (!w) return <div className="sat-label text-center py-16" data-testid="spacewalk-not-found">EVA RECORD NOT FOUND</div>;

  return (
    <div className="pb-10" data-testid="page-spacewalk-detail">
      <Link to="/roster" className="sat-label inline-flex items-center gap-2 mb-6 hover:sat-cyan" data-testid="back-roster">
        <ArrowLeft size={14} /> BACK TO ROSTER
      </Link>

      <div className="sat-surface sat-brackets p-6 md:p-10 mb-6">
        <div className="sat-label sat-cyan mb-2">EXTRA-VEHICULAR ACTIVITY</div>
        <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight mb-4" data-testid="spacewalk-name">{w.name}</h1>
        <div className="flex flex-wrap gap-6 sat-label">
          <div><Clock size={11} className="inline mr-2 sat-cyan"/> DURATION · <span className="sat-cyan">{fmtDuration(w.duration)}</span></div>
          {w.location && <div><MapPin size={11} className="inline mr-2 sat-cyan"/> STATION · {w.location}</div>}
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          <FavHeart kind="spacewalk" ref_id={w.id} title={w.name} size={14} testid="spacewalk-fav" />
          <ShareButton url={typeof window !== "undefined" ? window.location.href : ""} title={w.name} size={14} testid="spacewalk-share" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <section className="sat-surface p-6" data-testid="spacewalk-timeline">
          <div className="sat-label sat-cyan mb-4">TIMELINE</div>
          <div className="space-y-3">
            <div>
              <div className="sat-label">HATCH OPEN</div>
              <div className="font-mono text-sm mt-1 sat-cyan">{formatDateTime(w.start)}</div>
            </div>
            <div>
              <div className="sat-label">HATCH CLOSE</div>
              <div className="font-mono text-sm mt-1 sat-cyan">{formatDateTime(w.end)}</div>
            </div>
            {w.expedition?.name && (
              <div>
                <div className="sat-label">EXPEDITION</div>
                <div className="font-mono text-sm mt-1">{w.expedition.name}</div>
              </div>
            )}
          </div>
        </section>
        <section className="sat-surface p-6" data-testid="spacewalk-crew">
          <div className="sat-label sat-cyan mb-4">CREW · {(w.crew || []).length}</div>
          <div className="space-y-2">
            {(w.crew || []).map((c, i) => {
              const ast = c.astronaut || c;
              return (
                <Link key={ast.id || i} to={ast.id ? `/roster/astronaut/${ast.id}` : "#"}
                  className="flex items-center gap-3 p-2 border border-[color:var(--sat-border)] hover:border-[color:var(--sat-cyan)] transition-colors"
                  data-testid={`spacewalk-crew-${i}`}>
                  <div className="w-10 h-10 bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
                    {ast.profile_image ? <img src={ast.profile_image} alt={ast.name} className="w-full h-full object-cover"/> : <User size={16} className="text-[color:var(--sat-cyan)]"/>}
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-sm">{ast.name || "UNKNOWN"}</div>
                    <div className="sat-label mt-0.5">{c.role || ast.agency?.name || ""}</div>
                  </div>
                </Link>
              );
            })}
            {(w.crew || []).length === 0 && <div className="sat-label">NO CREW ON RECORD</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
